import React, { useEffect, useMemo, useState } from "react";
import TenantAdminSectionHeader from "./TenantAdminSectionHeader";
import { useUser } from "../../context/UserContext";
import { getRoleNames, hasAnyPermission } from "../../utils/tenantAdminAccess";
import { TENANT_GOVERNANCE_PERMISSION_GROUPS } from "../../constants/tenantGovernancePermissions";
import {
  DOCUMENT_LIBRARY_CATEGORIES,
  DOCUMENT_LIBRARY_CATEGORY_BY_KEY,
} from "../../constants/documentLibraryCategories";
import {
  archiveTenantDocument,
  createTenantDocument,
  listTenantDocuments,
} from "../../services/documentLibraryService";

const DOCUMENT_TYPE_OPTIONS = [
  "general",
  "workorder",
  "timesheet",
  "inspection_report",
  "certificate",
  "drawing",
  "revision",
  "toolbox",
  "safety_instruction",
  "permit",
  "customer_document",
  "project_document",
  "asset_document",
  "template",
  "other",
];

const FILE_TYPE_OPTIONS = ["pdf", "docx", "xlsx", "image", "other"];
const STATUS_OPTIONS = ["concept", "ter_controle", "goedgekeurd", "vervallen", "gearchiveerd"];

const DEFAULT_FORM = {
  title: "",
  description: "",
  documentType: "general",
  categoryKey: "other",
  fileType: "other",
  status: "concept",
  visibility: {
    monteur: false,
    inspecteur: false,
    projectleider: true,
    tenantAdmin: true,
    customerPortal: false,
  },
  linkedTo: {
    customerId: "",
    projectId: "",
    workorderId: "",
    assetId: "",
    inspectionId: "",
    timesheetId: "",
    toolboxId: "",
    userId: "",
  },
};

function linkedLabel(item) {
  const linkedTo = item?.linkedTo || {};
  const labels = [];
  if (linkedTo.customerId) labels.push("klant");
  if (linkedTo.projectId) labels.push("project");
  if (linkedTo.workorderId) labels.push("werkbon");
  if (linkedTo.assetId) labels.push("installatie");
  if (linkedTo.inspectionId) labels.push("inspectie");
  if (linkedTo.timesheetId) labels.push("mandagstaat");
  if (linkedTo.toolboxId) labels.push("toolbox");
  if (linkedTo.userId) labels.push("gebruiker");
  return labels.length ? labels.join(", ") : "-";
}

function visibilityLabel(item) {
  const visibility = item?.visibility || {};
  const labels = [];
  if (visibility.monteur) labels.push("monteur");
  if (visibility.inspecteur) labels.push("inspecteur");
  if (visibility.projectleider) labels.push("projectleider");
  if (visibility.tenantAdmin) labels.push("tenant-admin");
  if (visibility.customerPortal) labels.push("klantportaal");
  return labels.length ? labels.join(", ") : "-";
}

function toInputValue(value) {
  return String(value || "").trim();
}

export default function TenantAdminDocumentLibraryPage() {
  const { user } = useUser();
  const roleNames = useMemo(() => new Set(getRoleNames(user)), [user]);
  const isFullTenantAdmin = roleNames.has("tenantadmin") || roleNames.has("platformadmin");

  const canView = useMemo(
    () => isFullTenantAdmin || hasAnyPermission(user, TENANT_GOVERNANCE_PERMISSION_GROUPS.documentsView),
    [isFullTenantAdmin, user]
  );
  const canCreate = useMemo(
    () => isFullTenantAdmin || hasAnyPermission(user, TENANT_GOVERNANCE_PERMISSION_GROUPS.documentsCreate),
    [isFullTenantAdmin, user]
  );
  const canUpdate = useMemo(
    () => isFullTenantAdmin || hasAnyPermission(user, TENANT_GOVERNANCE_PERMISSION_GROUPS.documentsUpdate),
    [isFullTenantAdmin, user]
  );
  const canArchive = useMemo(
    () => isFullTenantAdmin || hasAnyPermission(user, TENANT_GOVERNANCE_PERMISSION_GROUPS.documentsArchive),
    [isFullTenantAdmin, user]
  );

  const [filters, setFilters] = useState({
    category: "",
    documentType: "",
    status: "",
    fileType: "",
    search: "",
  });
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  const [form, setForm] = useState(DEFAULT_FORM);

  const loadDocuments = async () => {
    if (!canView) return;
    setLoading(true);
    setError("");
    try {
      const query = {};
      if (filters.category) query.category = filters.category;
      if (filters.documentType) query.documentType = filters.documentType;
      if (filters.status) query.status = filters.status;
      if (filters.fileType) query.fileType = filters.fileType;
      if (filters.search) query.search = filters.search;
      const data = await listTenantDocuments(query);
      setItems(Array.isArray(data?.items) ? data.items : []);
    } catch (err) {
      setError(err?.response?.data?.message || "Documentbibliotheek laden mislukt.");
      setItems([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadDocuments();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canView]);

  const submitCreate = async (event) => {
    event.preventDefault();
    if (!canCreate) return;

    setSaving(true);
    setError("");
    setMessage("");
    try {
      const payload = {
        title: form.title,
        description: form.description,
        documentType: form.documentType,
        category: {
          key: form.categoryKey,
          label: DOCUMENT_LIBRARY_CATEGORY_BY_KEY[form.categoryKey]?.label || "Overig",
        },
        fileType: form.fileType,
        status: form.status,
        visibility: form.visibility,
        linkedTo: Object.fromEntries(
          Object.entries(form.linkedTo).map(([key, value]) => [key, toInputValue(value)])
        ),
      };
      const data = await createTenantDocument(payload);
      setMessage(data?.message || "Document geregistreerd.");
      setForm(DEFAULT_FORM);
      await loadDocuments();
    } catch (err) {
      setError(err?.response?.data?.message || "Document registreren mislukt.");
    } finally {
      setSaving(false);
    }
  };

  const archiveItem = async (id) => {
    if (!canArchive || !id) return;
    setError("");
    setMessage("");
    try {
      const data = await archiveTenantDocument(id);
      setMessage(data?.message || "Document gearchiveerd.");
      await loadDocuments();
    } catch (err) {
      setError(err?.response?.data?.message || "Archiveren mislukt.");
    }
  };

  return (
    <div className="mx-auto max-w-7xl px-4 py-6 sm:px-6 lg:px-8">
      <TenantAdminSectionHeader
        title="Documentbibliotheek"
        description="Beheer centrale documenten, templates en bijlagen die later gebruikt worden bij werkbonnen, mandagstaten, inspecties en rapportages."
        backTo="/tenant-admin"
      />

      <div className="mb-6 rounded-2xl border border-slate-200 bg-white p-4 text-sm text-slate-700 shadow-sm">
        Documenten worden centraal opgeslagen en gekoppeld aan klant, project, werkbon, installatie, inspectie of gebruiker.
      </div>

      {error ? (
        <div className="mb-4 rounded-xl border border-red-300 bg-red-50 px-3 py-2 text-sm text-red-700">{error}</div>
      ) : null}
      {message ? (
        <div className="mb-4 rounded-xl border border-emerald-300 bg-emerald-50 px-3 py-2 text-sm text-emerald-700">{message}</div>
      ) : null}

      <section className="mb-6 rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
        <h2 className="mb-3 text-lg font-semibold text-slate-900">Filters</h2>
        <div className="grid gap-3 md:grid-cols-5">
          <select
            value={filters.category}
            onChange={(event) => setFilters((prev) => ({ ...prev, category: event.target.value }))}
            className="rounded-lg border border-slate-300 px-3 py-2 text-sm"
          >
            <option value="">Alle categorieen</option>
            {DOCUMENT_LIBRARY_CATEGORIES.map((category) => (
              <option key={category.key} value={category.key}>{category.label}</option>
            ))}
          </select>
          <select
            value={filters.documentType}
            onChange={(event) => setFilters((prev) => ({ ...prev, documentType: event.target.value }))}
            className="rounded-lg border border-slate-300 px-3 py-2 text-sm"
          >
            <option value="">Alle documenttypes</option>
            {DOCUMENT_TYPE_OPTIONS.map((type) => (
              <option key={type} value={type}>{type}</option>
            ))}
          </select>
          <select
            value={filters.status}
            onChange={(event) => setFilters((prev) => ({ ...prev, status: event.target.value }))}
            className="rounded-lg border border-slate-300 px-3 py-2 text-sm"
          >
            <option value="">Alle statussen</option>
            {STATUS_OPTIONS.map((status) => (
              <option key={status} value={status}>{status}</option>
            ))}
          </select>
          <select
            value={filters.fileType}
            onChange={(event) => setFilters((prev) => ({ ...prev, fileType: event.target.value }))}
            className="rounded-lg border border-slate-300 px-3 py-2 text-sm"
          >
            <option value="">Alle bestandstypes</option>
            {FILE_TYPE_OPTIONS.map((type) => (
              <option key={type} value={type}>{type}</option>
            ))}
          </select>
          <input
            value={filters.search}
            onChange={(event) => setFilters((prev) => ({ ...prev, search: event.target.value }))}
            className="rounded-lg border border-slate-300 px-3 py-2 text-sm"
            placeholder="Zoeken op titel of omschrijving"
          />
        </div>
        <div className="mt-3 flex gap-2">
          <button
            type="button"
            onClick={loadDocuments}
            className="rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
            disabled={!canView}
          >
            Filter toepassen
          </button>
        </div>
      </section>

      <section className="mb-6 overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-slate-200 text-sm">
            <thead className="bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-600">
              <tr>
                <th className="px-3 py-2">Titel</th>
                <th className="px-3 py-2">Categorie</th>
                <th className="px-3 py-2">Type</th>
                <th className="px-3 py-2">Status</th>
                <th className="px-3 py-2">Gekoppeld aan</th>
                <th className="px-3 py-2">Zichtbaar voor</th>
                <th className="px-3 py-2">Laatst gewijzigd</th>
                <th className="px-3 py-2">Acties</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {loading ? (
                <tr>
                  <td className="px-3 py-4 text-slate-500" colSpan={8}>Laden...</td>
                </tr>
              ) : items.length === 0 ? (
                <tr>
                  <td className="px-3 py-4 text-slate-500" colSpan={8}>Geen documenten gevonden.</td>
                </tr>
              ) : (
                items.map((item) => (
                  <tr key={item._id}>
                    <td className="px-3 py-2 font-medium text-slate-900">{item.title}</td>
                    <td className="px-3 py-2 text-slate-700">{item?.category?.label || "-"}</td>
                    <td className="px-3 py-2 text-slate-700">{item.documentType}</td>
                    <td className="px-3 py-2 text-slate-700">{item.status}</td>
                    <td className="px-3 py-2 text-slate-700">{linkedLabel(item)}</td>
                    <td className="px-3 py-2 text-slate-700">{visibilityLabel(item)}</td>
                    <td className="px-3 py-2 text-slate-700">{item.updatedAt ? new Date(item.updatedAt).toLocaleString() : "-"}</td>
                    <td className="px-3 py-2">
                      {canArchive && item.status !== "gearchiveerd" ? (
                        <button
                          type="button"
                          onClick={() => archiveItem(item._id)}
                          className="rounded border border-amber-300 bg-amber-50 px-2 py-1 text-xs font-medium text-amber-800 hover:bg-amber-100"
                        >
                          Archiveer
                        </button>
                      ) : (
                        <span className="text-xs text-slate-400">-</span>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </section>

      <section className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
        <div className="mb-3 flex items-center justify-between gap-2">
          <h2 className="text-lg font-semibold text-slate-900">Document registreren</h2>
          {!canCreate ? (
            <span className="rounded-full border border-slate-300 bg-slate-100 px-2 py-1 text-xs text-slate-600">
              Alleen lezen
            </span>
          ) : null}
        </div>

        <form onSubmit={submitCreate} className="space-y-4">
          <div className="grid gap-3 md:grid-cols-2">
            <input
              value={form.title}
              onChange={(event) => setForm((prev) => ({ ...prev, title: event.target.value }))}
              placeholder="Titel"
              className="rounded-lg border border-slate-300 px-3 py-2 text-sm"
              disabled={!canCreate || saving}
              required
            />
            <input
              value={form.description}
              onChange={(event) => setForm((prev) => ({ ...prev, description: event.target.value }))}
              placeholder="Omschrijving"
              className="rounded-lg border border-slate-300 px-3 py-2 text-sm"
              disabled={!canCreate || saving}
            />
            <select
              value={form.documentType}
              onChange={(event) => setForm((prev) => ({ ...prev, documentType: event.target.value }))}
              className="rounded-lg border border-slate-300 px-3 py-2 text-sm"
              disabled={!canCreate || saving}
            >
              {DOCUMENT_TYPE_OPTIONS.map((type) => (
                <option key={type} value={type}>{type}</option>
              ))}
            </select>
            <select
              value={form.categoryKey}
              onChange={(event) => setForm((prev) => ({ ...prev, categoryKey: event.target.value }))}
              className="rounded-lg border border-slate-300 px-3 py-2 text-sm"
              disabled={!canCreate || saving}
              required
            >
              {DOCUMENT_LIBRARY_CATEGORIES.map((category) => (
                <option key={category.key} value={category.key}>{category.label}</option>
              ))}
            </select>
            <select
              value={form.fileType}
              onChange={(event) => setForm((prev) => ({ ...prev, fileType: event.target.value }))}
              className="rounded-lg border border-slate-300 px-3 py-2 text-sm"
              disabled={!canCreate || saving}
            >
              {FILE_TYPE_OPTIONS.map((type) => (
                <option key={type} value={type}>{type}</option>
              ))}
            </select>
            <select
              value={form.status}
              onChange={(event) => setForm((prev) => ({ ...prev, status: event.target.value }))}
              className="rounded-lg border border-slate-300 px-3 py-2 text-sm"
              disabled={!canCreate || saving}
            >
              {STATUS_OPTIONS.map((status) => (
                <option key={status} value={status}>{status}</option>
              ))}
            </select>
          </div>

          <div>
            <p className="mb-2 text-sm font-medium text-slate-700">Zichtbaarheid</p>
            <div className="grid gap-2 md:grid-cols-5">
              {Object.keys(form.visibility).map((key) => (
                <label key={key} className="flex items-center gap-2 rounded border border-slate-200 bg-slate-50 px-2 py-1.5 text-sm">
                  <input
                    type="checkbox"
                    checked={form.visibility[key] === true}
                    onChange={(event) =>
                      setForm((prev) => ({
                        ...prev,
                        visibility: { ...prev.visibility, [key]: event.target.checked },
                      }))
                    }
                    disabled={!canCreate || saving}
                  />
                  <span>{key}</span>
                </label>
              ))}
            </div>
          </div>

          <div>
            <p className="mb-2 text-sm font-medium text-slate-700">Optionele koppelingen (ObjectId)</p>
            <div className="grid gap-2 md:grid-cols-2">
              {Object.keys(form.linkedTo).map((key) => (
                <input
                  key={key}
                  value={form.linkedTo[key]}
                  onChange={(event) =>
                    setForm((prev) => ({
                      ...prev,
                      linkedTo: { ...prev.linkedTo, [key]: event.target.value },
                    }))
                  }
                  placeholder={key}
                  className="rounded-lg border border-slate-300 px-3 py-2 text-sm"
                  disabled={!canCreate || saving}
                />
              ))}
            </div>
          </div>

          <div className="flex gap-2">
            <button
              type="submit"
              disabled={!canCreate || saving}
              className="rounded-lg border border-slate-300 bg-slate-900 px-3 py-2 text-sm font-medium text-white hover:bg-slate-800 disabled:opacity-60"
            >
              {saving ? "Opslaan..." : "Document registreren"}
            </button>
            <button
              type="button"
              disabled={!canCreate || saving}
              onClick={() => setForm(DEFAULT_FORM)}
              className="rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-700 hover:bg-slate-50 disabled:opacity-60"
            >
              Reset
            </button>
          </div>
          {!canUpdate ? (
            <p className="text-xs text-slate-500">Deze MVP-pagina respecteert read-only toegang: zonder update-rechten zijn bewerkacties beperkt.</p>
          ) : null}
        </form>
      </section>
    </div>
  );
}
